<template>
  <div class="activity-widget">
    <PConfirmDialog />
    <CDropdown
      variant="nav-item"
      placement="bottom-end"
    >
      <CDropdownToggle

        :caret="false"
        class="text-white position-relative"
      >
        <CIcon icon="cil-bell" size="lg" />
        <CBadge
          v-if="atividadesStore.totalNaoLidasUsuario > 0"
          color="danger"
          position="top-end"
          shape="rounded-pill"
          class="activity-badge"
        >
          {{ formatarNumero(atividadesStore.totalNaoLidasUsuario) }}
        </CBadge>
      </CDropdownToggle>

      <CDropdownMenu class="activity-dropdown">
        <CDropdownHeader class="d-flex justify-content-between align-items-center">
          <span class="fw-bold">Atividades Recentes</span>
          <CButton
            v-if="atividadesStore.totalNaoLidasUsuario > 0"
            size="sm"
            color="link"
            class="p-0 text-decoration-none btn-mark-all ps-3"
            @click.stop="marcarTodasComoLidas"
          >
            <CIcon icon="cil-check-circle" size="sm" class="me-0" />
            Marcar todas como lidas
          </CButton>
        </CDropdownHeader>

        <div v-if="loading" class="text-center p-3">
          <CSpinner size="sm" />
          <div class="small text-muted mt-1">
            Carregando...
          </div>
        </div>

        <div v-else-if="!atividadesStore.ultimasAtividadesUsuario?.length" class="text-center p-3 text-muted">
          <CIcon icon="cil-info" class="mb-1" />
          <div class="small">
            Nenhuma atividade recente
          </div>
        </div>

        <template v-else>
          <div
            ref="scrollContainer"
            class="activity-scroll-container"
            @scroll="handleScroll"
          >
            <CDropdownItem
              v-for="atividade in displayedActivities"
              :key="`${atividade.tipo}-${atividade.id}`"
              class="activity-item"
              :class="{ 'activity-unread': !atividade.lida }"
              @click.stop
            >
              <div class="d-flex align-items-start">
                <div class="activity-icon me-2">
                  <font-awesome-icon
                    :icon="atividadesStore.getIconForActivity(atividade)"
                    class="text-muted"
                  />
                </div>
                <div class="flex-grow-1 me-3">
                  <div class="activity-description" @click.stop="atividade.acao !== 'Excluiu' ? abrirDetalhes(atividade) : null">
                    {{ atividade.descricao }}
                  </div>
                  <div class="activity-meta text-muted">
                    <small>
                      <CIcon icon="cil-clock" size="sm" class="me-1" />
                      {{ atividadesStore.formatarTempo(atividade.data) }}
                    </small>
                  </div>
                </div>
                <div class="d-flex gap-1 flex-shrink-0">
                  <!-- Botão para marcar como lida -->
                  <CButton
                    v-if="!atividade.lida"
                    v-c-tooltip="'Marcar como lida'"
                    size="sm"
                    color="success"
                    shape="rounded-pill"
                    class="btn-action"
                    @click.stop="marcarComoLida(atividade.id)"
                  >
                    <CIcon icon="cil-check" size="sm" />
                  </CButton>
                  <!-- Botão para marcar como não lida -->
                  <CButton
                    v-if="atividade.lida"
                    v-c-tooltip="'Marcar como não lida'"
                    size="sm"
                    color="info"
                    shape="rounded-pill"
                    class="btn-action"
                    @click.stop="marcarComoNaoLida(atividade.id)"
                  >
                    <CIcon icon="cil-envelope-open" size="sm" />
                  </CButton>
                </div>
              </div>
            </CDropdownItem>

            <!-- Loading indicator para lazy loading -->
            <div v-if="loadingMore" class="text-center p-2">
              <CSpinner size="sm" />
              <div class="small text-muted">
                Carregando mais...
              </div>
            </div>
          </div>

          <CDropdownDivider />

          <CDropdownItem
            class="text-center text-primary"
            @click="irParaTimelineCompleta"
          >
            <CIcon icon="cil-list" class="me-1" />
            Ver todas as atividades
          </CDropdownItem>
        </template>
      </CDropdownMenu>
    </CDropdown>

    <!-- Modal de Receita Fixa -->
    <CModal
      v-model:visible="modalReceitaFixa.exibir"
      size="lg"
      alignment="center"
      @close="fecharModalReceitaFixa"
    >
      <CModalHeader>
        <CModalTitle>
          <font-awesome-icon icon="fas fa-dollar-sign" class="me-2" />
          Detalhes
        </CModalTitle>
      </CModalHeader>
      <CModalBody v-if="modalReceitaFixa.receita">
        <CRow>
          <CCol md="12" class="mb-3">
            <h6 class="text-muted mb-1">
              Descrição
            </h6>
            <p class="mb-0 fs-5">
              {{ modalReceitaFixa.receita.descricao || 'Sem descrição' }}
            </p>
          </CCol>
          <CCol md="6" class="mb-3">
            <h6 class="text-muted mb-1">
              Valor
            </h6>
            <p class="mb-0 text-success">
              R$ {{ parseFloat(modalReceitaFixa.receita.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
            </p>
          </CCol>
          <CCol md="6" class="mb-3">
            <h6 class="text-muted mb-1">
              Data
            </h6>
            <p class="mb-0">
              {{ modalReceitaFixa.receita.data ? new Date(modalReceitaFixa.receita.data).toLocaleDateString('pt-BR') : '-' }}
            </p>
          </CCol>
          <CCol v-if="modalReceitaFixa.receita.observacao" md="12" class="mb-3">
            <h6 class="text-muted mb-1">
              Observação
            </h6>
            <p class="mb-0">
              {{ modalReceitaFixa.receita.observacao }}
            </p>
          </CCol>
        </CRow>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" @click="fecharModalReceitaFixa">
          Fechar
        </CButton>
      </CModalFooter>
    </CModal>

    <!-- Modal de Receita BPO (Não-Fixa) -->
    <CModal
      v-model:visible="modalReceitaBpo.exibir"
      size="xl"
      alignment="center"
      @close="fecharModalReceitaBpo"
    >
      <CModalHeader>
        <CModalTitle>
          <font-awesome-icon icon="fas fa-dollar-sign" class="me-2" />
          Detalhes da Receita
        </CModalTitle>
      </CModalHeader>
      <CModalBody v-if="modalReceitaBpo.receita">
        <CRow>
          <!-- Informações Básicas -->
          <CCol md="12" class="mb-4">
            <h5 class="mb-3 text-primary">
              <font-awesome-icon icon="fas fa-info-circle" class="me-2" />
              Informações Gerais
            </h5>
            <CRow>
              <CCol md="8" class="mb-3">
                <h6 class="text-muted mb-1">
                  Descrição
                </h6>
                <p class="mb-0 fs-5">
                  {{ modalReceitaBpo.receita.descricao || 'Sem descrição' }}
                </p>
              </CCol>
              <CCol md="4" class="mb-3">
                <h6 class="text-muted mb-1">
                  Tipo
                </h6>
                <CBadge :color="modalReceitaBpo.receita.tipo === 'UNICA' ? 'success' : 'primary'">
                  {{ modalReceitaBpo.receita.tipo }}
                </CBadge>
              </CCol>
              <CCol md="6" class="mb-3">
                <h6 class="text-muted mb-1">
                  Valor Total
                </h6>
                <p class="mb-0 text-success">
                  R$ {{ parseFloat(modalReceitaBpo.receita.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                </p>
              </CCol>
              <CCol md="6" class="mb-3">
                <h6 class="text-muted mb-1">
                  Data
                </h6>
                <p class="mb-0">
                  {{ modalReceitaBpo.receita.data ? new Date(modalReceitaBpo.receita.data).toLocaleDateString('pt-BR') : '-' }}
                </p>
              </CCol>
            </CRow>
          </CCol>

          <!-- Pagador e Favorecido -->
          <CCol md="12" class="mb-4">
            <h5 class="mb-3 text-primary">
              <font-awesome-icon icon="fas fa-users" class="me-2" />
              Partes Envolvidas
            </h5>
            <CRow v-if="pagadorEFavorecidoSaoIguais(modalReceitaBpo.receita)">
              <CCol md="12" class="mb-3">
                <CCard>
                  <CCardBody>
                    <h6 class="card-title text-muted mb-2">
                      <font-awesome-icon icon="fas fa-user" class="me-2" />
                      Pagador/Favorecido
                    </h6>
                    <p v-if="modalReceitaBpo.receita.pagador" class="mb-1 fw-bold">
                      {{ modalReceitaBpo.receita.pagador.razao_nome }}
                    </p>
                    <p v-if="modalReceitaBpo.receita.pagador" class="mb-2 text-muted">
                      CPF/CNPJ: {{ formatarCpfCnpj(modalReceitaBpo.receita.pagador.cpf_cnpj) }}
                    </p>
                    <CBadge color="info" class="me-2">
                      <font-awesome-icon icon="fas fa-info-circle" class="me-1" />
                      Pagador e favorecido são a mesma pessoa
                    </CBadge>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
            <CRow v-else>
              <CCol md="6" class="mb-3">
                <CCard>
                  <CCardBody>
                    <h6 class="card-title text-muted mb-2">
                      <font-awesome-icon icon="fas fa-hand-holding-usd" class="me-2" />
                      Pagador
                    </h6>
                    <p v-if="modalReceitaBpo.receita.pagador" class="mb-1 fw-bold">
                      {{ modalReceitaBpo.receita.pagador.razao_nome }}
                    </p>
                    <p v-if="modalReceitaBpo.receita.pagador" class="mb-0 text-muted">
                      CPF/CNPJ: {{ formatarCpfCnpj(modalReceitaBpo.receita.pagador.cpf_cnpj) }}
                    </p>
                    <p v-else class="mb-0 text-muted">
                      Não informado
                    </p>
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol md="6" class="mb-3">
                <CCard>
                  <CCardBody>
                    <h6 class="card-title text-muted mb-2">
                      <font-awesome-icon icon="fas fa-user-check" class="me-2" />
                      Favorecido
                    </h6>
                    <p v-if="modalReceitaBpo.receita.favorecido" class="mb-1 fw-bold">
                      {{ modalReceitaBpo.receita.favorecido.razao_nome }}
                    </p>
                    <p v-if="modalReceitaBpo.receita.favorecido" class="mb-0 text-muted">
                      CPF/CNPJ: {{ formatarCpfCnpj(modalReceitaBpo.receita.favorecido.cpf_cnpj) }}
                    </p>
                    <p v-else class="mb-0 text-muted">
                      Não informado
                    </p>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </CCol>

          <!-- Serviços -->
          <CCol v-if="modalReceitaBpo.receita.servicos && modalReceitaBpo.receita.servicos.length > 0" md="12" class="mb-4">
            <h5 class="mb-3 text-primary">
              <font-awesome-icon icon="fas fa-clipboard-list" class="me-2" />
              Serviços
            </h5>
            <CTable striped hover>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Serviço</CTableHeaderCell>
                  <CTableHeaderCell class="text-center">
                    Quantidade
                  </CTableHeaderCell>
                  <CTableHeaderCell class="text-end">
                    Valor Unitário
                  </CTableHeaderCell>
                  <CTableHeaderCell class="text-end">
                    Valor Total
                  </CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                <CTableRow v-for="servico in modalReceitaBpo.receita.servicos" :key="servico.id">
                  <CTableDataCell>
                    {{ servico.servico ? servico.servico.nome : 'Serviço não especificado' }}
                  </CTableDataCell>
                  <CTableDataCell class="text-center">
                    {{ servico.quantidade || 1 }}
                  </CTableDataCell>
                  <CTableDataCell class="text-end">
                    R$ {{ calcularValorUnitario(servico).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  </CTableDataCell>
                  <CTableDataCell class="text-end">
                    R$ {{ parseFloat(servico.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  </CTableDataCell>
                </CTableRow>
              </CTableBody>
            </CTable>
          </CCol>

          <!-- Forma de Pagamento -->
          <CCol v-if="modalReceitaBpo.receita.pagamentos && modalReceitaBpo.receita.pagamentos.length > 0" md="12" class="mb-3">
            <h5 class="mb-3 text-primary">
              <font-awesome-icon icon="fas fa-credit-card" class="me-2" />
              Formas de Pagamento
            </h5>
            <CListGroup>
              <CListGroupItem v-for="pagamento in modalReceitaBpo.receita.pagamentos" :key="pagamento.id">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <div class="d-flex align-items-center mb-1">
                      <font-awesome-icon :icon="getIconePagamento(pagamento.forma_de_pagamento)" class="me-2" />
                      <strong>{{ getLabelPagamento(pagamento.forma_de_pagamento) }}</strong>
                      <CBadge v-if="pagamento.parcela > 1" color="secondary" class="ms-2">
                        Parcela {{ pagamento.parcela }}
                      </CBadge>
                    </div>
                    <small v-if="pagamento.data_pagamento" class="text-muted">
                      <font-awesome-icon icon="fas fa-calendar" class="me-1" />
                      {{ new Date(pagamento.data_pagamento).toLocaleDateString('pt-BR') }}
                    </small>
                  </div>
                  <CBadge color="success" class="fs-6">
                    R$ {{ parseFloat(pagamento.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  </CBadge>
                </div>
              </CListGroupItem>
            </CListGroup>
          </CCol>
        </CRow>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" @click="fecharModalReceitaBpo">
          Fechar
        </CButton>
      </CModalFooter>
    </CModal>
  </div>
</template>

<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm'

const atividadesStore = useAtividadesStore()
const prestacaoStore = usePrestacaoDeContaStore()
const loading = ref(false)
const loadingMore = ref(false)
const scrollContainer = ref<HTMLElement>()
const confirm = useConfirm()

// Computed para mostrar as atividades paginadas para o infinite scroll
const displayedActivities = computed(() => {
  return atividadesStore.atividadesPaginadasData || []
})

// Estado para modal de receita fixa
const modalReceitaFixa = ref({
  exibir: false,
  receita: null as any,
})

// Estado para modal de receita BPO (não-fixa)
const modalReceitaBpo = ref({
  exibir: false,
  receita: null as any,
})

let intervalId: NodeJS.Timeout | null = null

const carregarResumo = async () => {
  loading.value = true
  await atividadesStore.carregarResumo()
  console.log('[AppHeaderActivityWidget] totalNaoLidasUsuario:', atividadesStore.totalNaoLidasUsuario)
  console.log('[AppHeaderActivityWidget] resumoUsuario:', atividadesStore.resumoUsuario)
  console.log('[AppHeaderActivityWidget] Atividades não lidas:', atividadesStore.resumoUsuario?.ultimas_atividades?.filter((a: any) => !a.lida))
  loading.value = false
}

// Função para detectar quando o usuário chegou ao final do scroll
const handleScroll = async (event: Event) => {
  const element = event.target as HTMLElement
  const { scrollTop, scrollHeight, clientHeight } = element

  // Se chegou perto do fim (90% do scroll)
  if (scrollTop + clientHeight >= scrollHeight * 0.9) {
    await carregarMaisAtividades()
  }
}

// Função para carregar mais atividades
const carregarMaisAtividades = async () => {
  if (loadingMore.value || !atividadesStore.hasMorePages) {
    return
  }

  loadingMore.value = true
  try {
    await atividadesStore.carregarMaisAtividades()
  } catch (error) {
    console.error('Erro ao carregar mais atividades:', error)
  } finally {
    loadingMore.value = false
  }
}

const formatarNumero = (num: number): string => {
  if (num > 99) {
    return '99+'
  }
  return num.toString()
}

const irParaTimelineCompleta = () => {
  navigateTo('/atividades')
}

const marcarComoLida = async (atividadeId: string) => {
  try {
    console.log('[AppHeaderActivityWidget] Marcando como lida - antes:', atividadesStore.totalNaoLidasUsuario)
    await atividadesStore.marcarComoLida(atividadeId)
    console.log('[AppHeaderActivityWidget] Marcando como lida - depois:', atividadesStore.totalNaoLidasUsuario)
  } catch (error) {
    console.error('Erro ao marcar atividade como lida:', error)
  }
}

const marcarComoNaoLida = async (atividadeId: string) => {
  try {
    await atividadesStore.marcarComoNaoLida(atividadeId)
  } catch (error) {
    console.error('Erro ao marcar atividade como não lida:', error)
  }
}

const marcarTodasComoLidas = () => {
  confirm.require({
    message: 'Deseja marcar todas as notificações como lidas?',
    header: 'Confirmação',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Sim',
    rejectLabel: 'Não',
    acceptClass: 'p-button-success',
    rejectClass: 'p-button-secondary',
    accept: async () => {
      try {
        console.log('[AppHeaderActivityWidget] Marcando todas - antes:', atividadesStore.totalNaoLidasUsuario)
        await atividadesStore.marcarTodasComoLidas()
        console.log('[AppHeaderActivityWidget] Marcando todas - depois:', atividadesStore.totalNaoLidasUsuario)
      } catch (error) {
        console.error('Erro ao marcar todas as atividades como lidas:', error)
      }
    },
  })
}

// Listener para eventos customizados (fallback quando store não está disponível)
const handleCustomPushEvent = (event: Event) => {
  const customEvent = event as CustomEvent
  console.log('[AppHeaderActivityWidget] Push notification recebida via evento customizado, atualizando atividades...')
  if (customEvent.detail && customEvent.detail.shouldUpdateActivities) {
    carregarResumo()
  }
}

onMounted(async () => {
  console.log('[AppHeaderActivityWidget] Montando componente...')

  // Carregar resumo para o widget
  await carregarResumo()

  // Inicializar as atividades paginadas para o dropdown
  await atividadesStore.carregarAtividadesPaginadas(1, 10, true)

  console.log('[AppHeaderActivityWidget] Estado inicial:', {
    totalNaoLidasUsuario: atividadesStore.totalNaoLidasUsuario,
    ultimasAtividadesUsuario: atividadesStore.ultimasAtividadesUsuario?.length,
  })

  // Atualizar a cada 2 minutos
  intervalId = setInterval(carregarResumo, 2 * 60 * 1000)

  // Escuta mensagens do Service Worker
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
  }

  // Escuta eventos customizados para fallback
  if (typeof window !== 'undefined') {
    window.addEventListener('pushNotificationReceived', handleCustomPushEvent)
  }
})

const abrirDetalhes = async (item: any) => {
  console.log('Abrindo detalhes para:', item)

  // Não permitir abrir detalhes para registros excluídos
  if (item.acao === 'Excluiu') {
    console.log('Registro foi excluído, não é possível abrir detalhes')
    return
  }

  if (item.tipo === 'bpo_despesa' && item.metadados?.despesa_id) {
    // Primeiro, carrega a despesa individual se não estiver no store
    if (!prestacaoStore.despesas?.find(d => d.id === item.metadados.despesa_id)) {
      try {
        const despesa = await useApiStore().request(`financeiro/despesas/${item.metadados.despesa_id}`) as any
        // Adiciona temporariamente ao store
        if (!prestacaoStore.despesas) {
          prestacaoStore.despesas = []
        }
        prestacaoStore.despesas.push(despesa as any)
      } catch (error) {
        console.error('Erro ao carregar despesa:', error)
        useStore().toasts.push({ content: 'Erro ao carregar detalhes da despesa', color: 'danger' })
        return
      }
    }
    prestacaoStore.abrirModalDetalhesDespesa(item.metadados.despesa_id)
  } else if (item.tipo === 'bpo_receita' && item.metadados?.receita_id) {
    try {
      // Carrega os dados da receita
      const receita = await useApiStore().request(`financeiro/receitas/${item.metadados.receita_id}`) as any

      // Verifica se é uma receita FIXA
      if (receita.tipo === 'FIXA') {
        // Abre modal específico para receita fixa
        abrirModalReceitaFixa(receita)
      } else {
        // Para receitas BPO não-fixas (UNICA, etc), abre modal detalhado
        abrirModalReceitaBpo(receita)
      }
    } catch (error) {
      console.error('Erro ao carregar receita:', error)
      useStore().toasts.push({ content: 'Erro ao carregar detalhes da receita', color: 'danger' })
    }
  }
}

const abrirModalReceitaFixa = (receita: any) => {
  modalReceitaFixa.value.receita = receita
  modalReceitaFixa.value.exibir = true
}

const fecharModalReceitaFixa = () => {
  modalReceitaFixa.value.exibir = false
  modalReceitaFixa.value.receita = null
}

const abrirModalReceitaBpo = (receita: any) => {
  modalReceitaBpo.value.receita = receita
  modalReceitaBpo.value.exibir = true
}

const fecharModalReceitaBpo = () => {
  modalReceitaBpo.value.exibir = false
  modalReceitaBpo.value.receita = null
}

// Função para formatar CPF/CNPJ
const formatarCpfCnpj = (valor: string) => {
  if (!valor) { return '-' }

  // Remove caracteres não numéricos
  const numeros = valor.replace(/\D/g, '')

  // CPF
  if (numeros.length === 11) {
    return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  // CNPJ
  if (numeros.length === 14) {
    return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }

  return valor
}

// Função para obter ícone do tipo de pagamento
const getIconePagamento = (tipo: string) => {
  const icones: Record<string, string> = {
    DINHEIRO: 'fas fa-money-bill-wave',
    PIX: 'fas fa-qrcode',
    CARTAO_CREDITO: 'fas fa-credit-card',
    CARTAO_DEBITO: 'fas fa-credit-card',
    TRANSFERENCIA: 'fas fa-exchange-alt',
    BOLETO: 'fas fa-barcode',
    CHEQUE: 'fas fa-money-check',
  }
  return icones[tipo] || 'fas fa-dollar-sign'
}

// Função para obter label do tipo de pagamento
const getLabelPagamento = (tipo: string) => {
  const labels: Record<string, string> = {
    DINHEIRO: 'Dinheiro',
    PIX: 'PIX',
    CARTAO_CREDITO: 'Cartão de Crédito',
    CARTAO_DEBITO: 'Cartão de Débito',
    TRANSFERENCIA: 'Transferência Bancária',
    BOLETO: 'Boleto',
    CHEQUE: 'Cheque',
  }
  return labels[tipo] || tipo
}

// Função para calcular valor unitário
const calcularValorUnitario = (servico: any) => {
  const valor = parseFloat(servico.valor || 0)
  const quantidade = parseInt(servico.quantidade || 1)
  return quantidade > 0 ? valor / quantidade : 0
}

// Função para verificar se pagador e favorecido são a mesma pessoa
const pagadorEFavorecidoSaoIguais = (receita: any) => {
  if (!receita?.pagador || !receita?.favorecido) {
    return false
  }
  return receita.pagador.id === receita.favorecido.id
}

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }

  // Remove o listener do Service Worker
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage)
  }

  // Remove o listener de eventos customizados
  if (typeof window !== 'undefined') {
    window.removeEventListener('pushNotificationReceived', handleCustomPushEvent)
  }
})
</script>

<style scoped>
.activity-badge {
  font-size: 0.65rem;
  min-width: 1.2rem;
  height: 1.2rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.activity-dropdown {
  width: 400px;
  max-height: 400px;
  overflow-y: auto;
}

.activity-scroll-container {
  max-height: 300px;
  min-width: 400px;
  overflow-y: auto;
}

.activity-item {
  border: none;
  padding: 0.75rem 1rem;
}

.activity-item:hover {
  background-color: var(--cui-gray-100);
}

.activity-item.activity-unread {
  background-color: var(--cui-light);
  border-left: 3px solid var(--cui-primary);
}

.activity-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.activity-icon .icon,
.activity-icon svg {
  width: 16px !important;
  height: 16px !important;
  opacity: 1 !important;
}

.activity-icon .text-success {
  color: #198754 !important;
}

.activity-icon .text-warning {
  color: #fd7e14 !important;
}

.activity-icon .text-danger {
  color: #dc3545 !important;
}

.activity-icon .text-secondary {
  color: #6c757d !important;
}

.activity-description {
  font-size: 0.875rem;
  line-height: 1.3;
  margin-bottom: 0.25rem;
  max-width: 280px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.activity-meta {
  font-size: 0.75rem;
  line-height: 1;
}

.activity-meta .icon {
  opacity: 0.6;
}

:deep(.dropdown-toggle::after) {
  display: none;
}

.btn-action {
  opacity: 0.8;
  min-width: 24px;
  min-height: 24px;
  padding: 0.2rem;
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-action .icon,
.btn-action svg {
  width: 12px !important;
  height: 12px !important;
  color: white !important;
  fill: white !important;
}

.btn-action:hover {
  opacity: 1;
  transform: scale(1.05);
}

.activity-item:hover .btn-action {
  opacity: 1;
}

.btn-mark-all {
  font-size: 0.75rem !important;
}

.activity-dropdown{
  min-width: 23em !important;
}
</style>
